import { getText } from '@storebrand-digital/fms-translations';
import { InsuranceDetailsView, MotorcycleDetails } from 'Api/insurance';
import { updateOwnRiskValues } from 'Components/InsuranceDetailsSections/components/InsuranceViewContent/insuranceViewContent.helpers';
import { setDefinedCoverageSection } from 'Components/InsuranceDetailsSections/helpers/insuranceDetailsSections.coverage.helpers';
import {
  getLabelValuePairForBooleanInsuranceField,
  setOwnContribution,
} from 'Components/InsuranceDetailsSections/helpers/insuranceDetailsSections.mapToInsuranceDetailsView.helpers';
import {
  DETAILED_SECONDARY_COVERAGE_LABEL_FMS_KEY,
  OwnContributionValues,
} from 'Components/InsuranceDetailsSections/insuranceDetailsSections.constants';
import { TfaRegistry } from 'Components/InsuranceDetailsSections/insuranceDetailsSections.types';
import { MOTORCYCLE_COVERAGES } from 'Constants/coverageProduct.constants';
import {
  MotorcycleCoverageProduct,
  VehicleCoverage,
} from 'Types/coverages/coverages.types';
import { formatMoney } from 'Utils/formatting/formatting.utils';
import { getAppText } from 'Utils/text';
import { mapBaseCarMotorcycleValuesToInsuranceDetailsView } from './mapBaseCarMotorcycleValuesToInsuranceDetailsView';

export function mapMotorcycleToInsuranceDetailsView(
  insurance: MotorcycleDetails,
  tfaRegistry: TfaRegistry[] | undefined,
  cwpDetailsToggle: boolean,
): InsuranceDetailsView {
  const getCoverages = updateOwnRiskValues(
    insurance.riskList.flatMap((risk) => risk.coverages),
  );

  const getOwnContributionValue = getCoverages.find(
    (coverage) =>
      MOTORCYCLE_COVERAGES.includes(
        coverage.coverageProduct as MotorcycleCoverageProduct,
      ) &&
      coverage.ownRisk &&
      coverage.ownRisk.value,
  )?.ownRisk.value;

  const updatedInsurance = {
    ...insurance,
    ownContribution: Number(getOwnContributionValue),
  };

  return {
    ...mapBaseCarMotorcycleValuesToInsuranceDetailsView(
      updatedInsurance,
      tfaRegistry,
      cwpDetailsToggle,
    ),
    secondary: [
      {
        label: getText(DETAILED_SECONDARY_COVERAGE_LABEL_FMS_KEY),
        value: mapMotorcycleCoverageToText(
          updatedInsurance.coverage as VehicleCoverage,
        ),
      },
      setOwnContribution(updatedInsurance.ownContribution),
      {
        label: getText('detailed.secondary.usabilityCode.label'),
        value: getText(`detailed.secondary.${updatedInsurance.usabilityCode}`, {
          insert: { usabilityCode: updatedInsurance.usabilityCode },
        }),
      },
      getLabelValuePairForBooleanInsuranceField(
        'garage',
        updatedInsurance.garage,
      ),
      getLabelValuePairForBooleanInsuranceField(
        'mcLocker',
        updatedInsurance.mcLocker,
      ),
    ],
    coverageSection: setDefinedCoverageSection(updatedInsurance),
  };
}

export const mapMotorcycleCoverageToText = (
  coverage: VehicleCoverage,
): string => {
  switch (coverage) {
    case VehicleCoverage.DRIVER_AND_PASSENGER_ACCIDENT:
    case VehicleCoverage.DRIVER_AND_PASSENGER_ACCIDENT_DEREG:
    case VehicleCoverage.LIABILITY:
    case VehicleCoverage.LIABILITY_DEREG:
      return getAppText('insurance.coverage.motorcycle.ansvar');
    case VehicleCoverage.REDUCED_OWN_DAMAGE:
      return getAppText('insurance.coverage.motorcycle.delkasko');
    default:
      return getAppText('insurance.coverage.motorcycle.kasko');
  }
};

export const getOwnContributionMotorcycleValue = (
  insurance: MotorcycleDetails,
): string => {
  const { coverage, ownContribution } = insurance;
  if (coverage === VehicleCoverage.FULL_OWN_DAMAGE) {
    return getText('detailed.secondary.ownContribution.value', {
      insert: { ownContribution: formatMoney(ownContribution) },
    });
  }

  if (coverage === VehicleCoverage.REDUCED_OWN_DAMAGE) {
    return OwnContributionValues.REDUCED_OWN_DAMAGE;
  }

  if (coverage === VehicleCoverage.DRIVER_AND_PASSENGER_ACCIDENT) {
    return OwnContributionValues.DRIVER_AND_PASSENGER_ACCIDENT;
  }
  return '';
};
